import React, { useState } from "react";
import { Layers, Plus, Trash2, Copy, ChevronUp, ChevronDown, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import InfoDot from "@/studio/InfoDot";

const PLETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const LABELS = ["Intro", "Verse", "Pre-Hook", "Hook", "Chorus", "Drop", "Bridge", "Build", "Break", "Outro", "Custom"];

const COLORS = {
  Intro: "#22d3ee",
  Verse: "#00e5ff",
  "Pre-Hook": "#a855f7",
  Hook: "#ff007f",
  Chorus: "#ff007f",
  Drop: "#ffb800",
  Bridge: "#00ff66",
  Build: "#ff9500",
  Break: "#8a8a8a",
  Outro: "#a855f7",
  Custom: "#8a8a8a",
};

const QUICK_ADD = ["Intro", "Verse", "Hook", "Drop", "Bridge", "Outro"];
const DEFAULT_PATTERN = { Intro: 0, Verse: 0, "Pre-Hook": 1, Hook: 1, Chorus: 1, Drop: 2, Bridge: 3, Build: 2, Break: 0, Outro: 0, Custom: 0 };

const rid = () => `sec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

function deriveFromEngine(engine) {
  const pb = engine.started ? engine.patternBars || 2 : 2;
  if (engine.started && Array.isArray(engine.sections) && engine.sections.length) {
    return engine.sections.map((s) => ({ ...s, id: s.id || rid() }));
  }
  const chain = engine.started ? engine.chain || [0] : [0, 0];
  const groups = [];
  chain.forEach((p) => {
    const last = groups[groups.length - 1];
    if (last && last.pattern === p) last.bars += pb;
    else groups.push({ pattern: p, bars: pb });
  });
  const guess = ["Intro", "Verse", "Hook", "Verse", "Bridge", "Outro"];
  return groups.map((g, i) => ({ id: rid(), label: guess[i] || "Verse", pattern: g.pattern, bars: g.bars }));
}

export default function SongSections() {
  const { engine, pushHistory, forceUpdate, play, isPlaying, stop } = useStudio();
  const [sections, setSections] = useState(() => deriveFromEngine(engine));
  const [applied, setApplied] = useState(false);
  const [dragId, setDragId] = useState(null);
  const pb = engine.started ? engine.patternBars || 2 : 2;
  const totalBars = sections.reduce((n, s) => n + s.bars, 0);

  const dirty = () => setApplied(false);

  const addSection = (label) => {
    setSections((s) => [...s, { id: rid(), label, pattern: DEFAULT_PATTERN[label] ?? 0, bars: pb * 2 }]);
    dirty();
  };

  const update = (id, patch) => {
    setSections((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    dirty();
  };

  const remove = (id) => {
    setSections((s) => s.filter((x) => x.id !== id));
    dirty();
  };

  const duplicate = (id) => {
    setSections((s) => {
      const i = s.findIndex((x) => x.id === id);
      if (i < 0) return s;
      const copy = { ...s[i], id: rid() };
      return [...s.slice(0, i + 1), copy, ...s.slice(i + 1)];
    });
    dirty();
  };

  const move = (id, dir) => {
    setSections((s) => {
      const i = s.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
    dirty();
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setSections((s) => {
      const from = s.findIndex((x) => x.id === dragId);
      const to = s.findIndex((x) => x.id === targetId);
      if (from < 0 || to < 0) return s;
      const copy = [...s];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
    setDragId(null);
    dirty();
  };

  const apply = () => {
    if (!sections.length) {
      toast.error("Add at least one section first");
      return;
    }
    engine.ensureStarted();
    pushHistory();
    engine.applySections(sections.map((s) => ({ ...s })));
    forceUpdate();
    setApplied(true);
    toast.success(`Arrangement set — ${totalBars} bars across ${sections.length} sections`);
  };

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="song-sections">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] shrink-0 flex-wrap gap-2">
        <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
          <Layers size={16} className="text-[#a855f7]" /> Song Sections
          <InfoDot section="sections" label="How Song Sections work" />
        </h3>
        <div className="flex items-center gap-3">
          <span className="font-mono-data text-[10px] text-neutral-500" data-testid="sections-total-bars">
            {totalBars} bars
          </span>
          <button
            data-testid="sections-apply-btn"
            onClick={apply}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-sm text-xs font-bold text-black transition-transform hover:scale-[1.03]"
            style={{ background: applied ? "linear-gradient(120deg,#00ff66,#00e5ff)" : "linear-gradient(120deg,#a855f7,#00e5ff)" }}
          >
            {applied ? <Check size={14} /> : <Wand2 size={14} />} {applied ? "Applied" : "Apply to Beat"}
          </button>
        </div>
      </div>

      {/* quick add */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#1a1a1a] shrink-0 flex-wrap">
        <span className="hw-label text-[9px] text-neutral-500 mr-1">Add</span>
        {QUICK_ADD.map((l) => (
          <button
            key={l}
            data-testid={`sections-add-${l.toLowerCase()}`}
            onClick={() => addSection(l)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-bold transition-colors"
            style={{ borderColor: `${COLORS[l]}55`, color: COLORS[l] }}
          >
            <Plus size={11} /> {l}
          </button>
        ))}
      </div>

      {/* section list */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto flex flex-col gap-2.5">
          {sections.length === 0 && (
            <div className="text-center py-16 text-neutral-500 text-sm">
              No sections yet. Use the buttons above to lay out your song — Intro, Verse, Hook, Drop…
            </div>
          )}
          {sections.map((s, idx) => {
            const color = COLORS[s.label] || "#8a8a8a";
            return (
              <div
                key={s.id}
                data-testid={`section-card-${idx}`}
                draggable
                onDragStart={() => setDragId(s.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(s.id)}
                className="rounded-sm border bg-[#0d0d0d] p-3 flex items-center gap-3 cursor-grab active:cursor-grabbing"
                style={{ borderColor: `${color}44` }}
              >
                <span className="font-mono-data text-[10px] text-neutral-600 w-5 text-center shrink-0">{idx + 1}</span>
                <span className="w-1.5 h-10 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />

                {/* label */}
                <select
                  data-testid={`section-label-${idx}`}
                  value={s.label}
                  onChange={(e) => update(s.id, { label: e.target.value, pattern: s.pattern })}
                  className="bg-[#050505] border border-[#262626] rounded-sm px-2 py-1.5 text-sm font-semibold outline-none focus:border-[#a855f7] transition-colors"
                  style={{ color }}
                >
                  {LABELS.map((l) => (
                    <option key={l} value={l} className="bg-[#0d0d0d] text-neutral-200">{l}</option>
                  ))}
                </select>

                {/* pattern */}
                <div className="flex items-center gap-1">
                  <span className="hw-label text-[8px] text-neutral-500 mr-0.5">Pat</span>
                  {PLETTERS.slice(0, 4).map((L, i) => (
                    <button
                      key={L}
                      data-testid={`section-pattern-${idx}-${L}`}
                      onClick={() => update(s.id, { pattern: i })}
                      className="w-6 h-6 rounded-sm text-[10px] font-bold border transition-colors"
                      style={
                        s.pattern === i
                          ? { borderColor: color, color: "#000", background: color }
                          : { borderColor: "#2a2a2a", color: "#737373" }
                      }
                    >
                      {L}
                    </button>
                  ))}
                </div>

                {/* bars */}
                <div className="flex items-center gap-1 ml-auto">
                  <span className="hw-label text-[8px] text-neutral-500">Bars</span>
                  <button
                    data-testid={`section-bars-minus-${idx}`}
                    onClick={() => update(s.id, { bars: Math.max(pb, s.bars - pb) })}
                    className="w-5 h-5 rounded-sm border border-[#2a2a2a] text-neutral-400 hover:text-white transition-colors"
                  >
                    −
                  </button>
                  <span className="font-mono-data text-[11px] text-neutral-200 w-5 text-center">{s.bars}</span>
                  <button
                    data-testid={`section-bars-plus-${idx}`}
                    onClick={() => update(s.id, { bars: Math.min(32, s.bars + pb) })}
                    className="w-5 h-5 rounded-sm border border-[#2a2a2a] text-neutral-400 hover:text-white transition-colors"
                  >
                    +
                  </button>
                </div>

                {/* controls */}
                <div className="flex items-center gap-0.5 shrink-0 pl-1 border-l border-[#1c1c1c]">
                  <button
                    data-testid={`section-up-${idx}`}
                    onClick={() => move(s.id, -1)}
                    disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-[#00e5ff] transition-colors disabled:opacity-25"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    data-testid={`section-down-${idx}`}
                    onClick={() => move(s.id, 1)}
                    disabled={idx === sections.length - 1}
                    className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-[#00e5ff] transition-colors disabled:opacity-25"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    data-testid={`section-duplicate-${idx}`}
                    onClick={() => duplicate(s.id)}
                    className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-[#ffb800] transition-colors"
                    title="Duplicate"
                  >
                    <Copy size={13} />
                  </button>
                  <button
                    data-testid={`section-delete-${idx}`}
                    onClick={() => remove(s.id)}
                    className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-[#ff3333] transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}

          {sections.length > 0 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                data-testid="sections-play-btn"
                onClick={() => (isPlaying ? stop() : play())}
                className="flex items-center gap-1.5 px-4 py-2 rounded-sm border border-[#00ff66] text-[#00ff66] text-xs font-bold hover:bg-[#00ff66]/10 transition-colors"
              >
                {isPlaying ? "Stop" : "Play arrangement"}
              </button>
              <p className="text-[11px] text-neutral-600">Drag cards to reorder · then Apply to Beat</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
